/**
 * FinancePanel — P&L quarter-to-date, quarterly reports, credit line,
 * exit offers, achievements.
 */

import { useState } from 'react';
import { toast } from '@/hooks/useToast';
import { Award, Handshake, Landmark } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PanelSection } from '@/components/game/ui';
import { useGameStore } from '@/store/gameStore';
import { money, num, pct, quarterLabel } from '@/game/format';
import { ACHIEVEMENTS } from '@/game/constants';

export function FinancePanel() {
  const state = useGameStore((s) => s.state)!;
  const drawCredit = useGameStore((s) => s.drawCredit);
  const repayCredit = useGameStore((s) => s.repayCredit);
  const acceptExit = useGameStore((s) => s.acceptExit);
  const declineExit = useGameStore((s) => s.declineExit);

  const [drawAmt, setDrawAmt] = useState(25_000);
  const [repayAmt, setRepayAmt] = useState(10_000);

  const qa = state.finance.qAccum;
  const gross = qa.revenue - qa.cogs;
  const creditRoom = state.finance.creditLimit - state.player.creditDrawn;

  return (
    <div className="space-y-6">
      {/* Exit offer */}
      {state.exitOffer && (
        <Card className="border-amber-500/60 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Handshake className="h-4 w-4 text-amber-600" /> Acquisition offer — {state.exitOffer.acquirer}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm">
              Indicative valuation <strong className="tabular-nums">{money(state.exitOffer.valuation)}</strong>{' '}
              ({state.exitOffer.multiple.toFixed(1)}× trailing-quarter revenue). Accept to end the run as an exit; decline and they may circle back later.
            </p>
            <div className="flex gap-2">
              <Button onClick={acceptExit}>Accept &amp; sign</Button>
              <Button variant="outline" onClick={declineExit}>Decline</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* QTD P&L */}
      <PanelSection title={`${quarterLabel(state.time.day).label} Quarter-to-Date`} desc="P&amp;L view: COGS is recognized at sale. Your cash ledger also carries inventory buys the day POs are placed.">
        <Card>
          <CardContent className="grid grid-cols-2 gap-x-6 gap-y-2 py-4 text-sm sm:grid-cols-3">
            <FinRow label="Revenue (net of refunds)" value={money(qa.revenue)} />
            <FinRow label="COGS" value={`-${money(qa.cogs)}`} />
            <FinRow label="Gross profit" value={money(gross)} strong />
            <FinRow label="Marketing" value={`-${money(qa.marketing)}`} />
            <FinRow label="Channel fees" value={`-${money(qa.channelFees)}`} />
            <FinRow label="Returns logistics" value={`-${money(qa.returnsCost)}`} />
            <FinRow label="Overhead + holding" value={`-${money(qa.overhead)}`} />
            <FinRow label="Interest" value={`-${money(qa.interest)}`} />
            <FinRow
              label="Net income (pre-tax)"
              value={money(qa.netIncome)}
              strong
              tone={qa.netIncome >= 0 ? 'good' : 'bad'}
            />
            <FinRow label="Units shipped" value={num(qa.unitsSold)} />
            <FinRow label="Return rate" value={qa.unitsSold > 0 ? pct(qa.unitsReturned / qa.unitsSold) : '—'} />
            <FinRow label="Blended CAC" value={qa.newCustomers > 0 ? money(qa.marketing / qa.newCustomers) : '—'} />
          </CardContent>
        </Card>
      </PanelSection>

      {/* Quarterly reports */}
      {state.history.quarterly.length > 0 && (
        <PanelSection title="Quarterly Reports" desc="The investor updates you never wanted to send.">
          <div className="overflow-x-auto rounded-xl border bg-card">
            <table className="w-full min-w-[540px] text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Quarter</th>
                  <th className="px-4 py-2 text-right font-medium">Revenue</th>
                  <th className="px-4 py-2 text-right font-medium">Net income</th>
                  <th className="px-4 py-2 text-right font-medium">CAC</th>
                  <th className="px-4 py-2 text-right font-medium">Returns</th>
                  <th className="px-4 py-2 text-right font-medium">Cash</th>
                </tr>
              </thead>
              <tbody>
                {[...state.history.quarterly].reverse().map((r) => (
                  <tr key={`${r.year}-q${r.quarter}`} className="border-b last:border-0">
                    <td className="px-4 py-2 font-medium">Q{r.quarter} {r.year}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(r.revenue)}</td>
                    <td className={cn('px-4 py-2 text-right tabular-nums', r.netIncome >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')}>
                      {money(r.netIncome)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.blendedCac > 0 ? money(r.blendedCac) : '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{pct(r.returnRate)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{money(r.endingCash)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </PanelSection>
      )}

      {/* Credit line */}
      <PanelSection title="Revolving Credit Line" desc="Expensive oxygen. Interest accrues daily on drawn balance.">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Landmark className="h-4 w-4" /> {(state.finance.aprDaily * 365 * 100).toFixed(1)}% APR</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm">
              Drawn <strong className="tabular-nums">{money(state.player.creditDrawn)}</strong> of{' '}
              {money(state.finance.creditLimit)} · available <span className="tabular-nums">{money(creditRoom)}</span>
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-32 space-y-1">
                <Input type="number" step={5000} value={drawAmt} onChange={(e) => setDrawAmt(Number(e.target.value))} aria-label="Draw amount" />
              </div>
              <Button size="sm" variant="outline" disabled={creditRoom <= 0} onClick={() => {
                const err = drawCredit(drawAmt);
                if (err) toast({ title: 'Draw failed', description: err, variant: 'destructive' });
              }}>Draw</Button>
              <div className="ml-4 w-32 space-y-1">
                <Input type="number" step={5000} value={repayAmt} onChange={(e) => setRepayAmt(Number(e.target.value))} aria-label="Repay amount" />
              </div>
              <Button size="sm" variant="outline" disabled={state.player.creditDrawn <= 0} onClick={() => {
                const err = repayCredit(repayAmt);
                if (err) toast({ title: 'Repayment failed', description: err, variant: 'destructive' });
              }}>Repay</Button>
            </div>
          </CardContent>
        </Card>
      </PanelSection>

      {/* Achievements */}
      <PanelSection title="Achievements" desc={`${state.achievements.length}/${ACHIEVEMENTS.length} unlocked`}>
        <div className="flex flex-wrap gap-2">
          {ACHIEVEMENTS.map((a) => {
            const got = state.achievements.includes(a.id);
            return (
              <Badge key={a.id} variant={got ? 'default' : 'outline'} className={cn('gap-1.5 px-3 py-1.5', !got && 'opacity-50')}>
                <Award className="h-3.5 w-3.5" />
                <span className="font-medium">{a.name}</span>
                {!got && <span className="text-[10px] opacity-70">{a.desc}</span>}
              </Badge>
            );
          })}
        </div>
      </PanelSection>
    </div>
  );
}

function FinRow({ label, value, strong, tone }: { label: string; value: string; strong?: boolean; tone?: 'good' | 'bad' }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-dashed pb-1.5 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('tabular-nums', strong ? 'font-semibold' : 'font-medium', tone === 'good' && 'text-emerald-600 dark:text-emerald-400', tone === 'bad' && 'text-red-600 dark:text-red-400')}>
        {value}
      </span>
    </div>
  );
}
